import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Space,
  Spin,
  Switch,
  message,
} from 'antd';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { activitiesApi, type ActivityInput } from '../../../api/endpoints/activities';
import { AccountSelect } from '../../../components/common/AccountSelect';
import { PersonSelect } from '../../../components/common/PersonSelect';
import { ProjectSelect } from '../../../components/common/ProjectSelect';
import { SaleSelect } from '../../../components/common/SaleSelect';
import { UserSelect } from '../../../components/common/UserSelect';
import { PageHeader } from '../../../components/common/PageHeader';
import { LookupSelect } from '../../../components/common/LookupSelect';

const schema = z.object({
  notes: z.string().min(1, 'Las notas son obligatorias'),
  type: z.string().optional().or(z.literal('')),
  accountId: z.number().int().optional().nullable(),
  personId: z.number().int().optional().nullable(),
  projectId: z.number().int().optional().nullable(),
  saleId: z.number().int().optional().nullable(),
  assignedToId: z.number().int().optional().nullable(),
  startDate: z.string().optional().or(z.literal('')),
  endDate: z.string().optional().or(z.literal('')),
  closed: z.boolean().optional(),
  private: z.boolean().optional(),
});

type FormValues = z.infer<typeof schema>;

function sanitize(values: FormValues): ActivityInput {
  const out: Partial<ActivityInput> = { notes: values.notes };
  if (values.type) out.type = values.type;
  if (values.accountId != null) out.accountId = values.accountId;
  if (values.personId != null) out.personId = values.personId;
  if (values.projectId != null) out.projectId = values.projectId;
  if (values.saleId != null) out.saleId = values.saleId;
  if (values.assignedToId != null) out.assignedToId = values.assignedToId;
  if (values.startDate) out.startDate = values.startDate;
  if (values.endDate) out.endDate = values.endDate;
  if (values.closed !== undefined) out.closed = values.closed;
  if (values.private !== undefined) out.private = values.private;
  return out as ActivityInput;
}

export function ActivityEditPage(): JSX.Element {
  const { id } = useParams();
  const activityId = id ? parseInt(id, 10) : null;
  const isEdit = activityId !== null;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const presetAccountId = searchParams.get('accountId');
  const presetPersonId = searchParams.get('personId');
  const presetProjectId = searchParams.get('projectId');
  const presetDate = searchParams.get('date');

  const { data, isLoading } = useQuery({
    queryKey: ['activity', activityId],
    queryFn: () => activitiesApi.findOne(activityId!),
    enabled: isEdit,
  });

  const { control, handleSubmit, reset, setValue } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      notes: '',
      type: 'TODO',
      accountId: null,
      personId: null,
      projectId: null,
      saleId: null,
      assignedToId: null,
      startDate: '',
      endDate: '',
      closed: false,
      private: false,
    },
  });

  const watchedAccountId = useWatch({ control, name: 'accountId' });
  const [lastAccountId, setLastAccountId] = useState<number | null | undefined>(undefined);

  useEffect(() => {
    // Si cambió la cuenta, limpiamos el contacto seleccionado para evitar inconsistencias
    if (lastAccountId !== undefined && lastAccountId !== watchedAccountId) {
      setValue('personId', null);
    }
    setLastAccountId(watchedAccountId);
  }, [watchedAccountId, lastAccountId, setValue]);

  useEffect(() => {
    if (data) {
      reset({
        notes: data.notes,
        type: data.type ?? '',
        accountId: data.accountId,
        personId: data.personId,
        projectId: data.projectId,
        saleId: data.saleId,
        assignedToId: data.assignedToId,
        startDate: data.startDate ?? '',
        endDate: data.endDate ?? '',
        closed: data.closed,
        private: data.private,
      });
      setLastAccountId(data.accountId);
    } else if (!isEdit && (presetAccountId || presetPersonId || presetProjectId || presetDate)) {
      // Modo nuevo con valores precargados desde URL (detalle de cuenta/proyecto o calendario)
      const accId = presetAccountId ? parseInt(presetAccountId, 10) : null;
      const perId = presetPersonId ? parseInt(presetPersonId, 10) : null;
      const prjId = presetProjectId ? parseInt(presetProjectId, 10) : null;
      reset({
        notes: '',
        type: 'TODO',
        accountId: accId,
        personId: perId,
        projectId: prjId,
        saleId: null,
        assignedToId: null,
        startDate: presetDate ?? '',
        endDate: '',
        closed: false,
        private: false,
      });
      setLastAccountId(accId);
    }
  }, [data, reset, isEdit, presetAccountId, presetPersonId, presetProjectId, presetDate]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload = sanitize(values);
      if (isEdit) return activitiesApi.update(activityId, payload);
      return activitiesApi.create(payload);
    },
    onSuccess: (saved) => {
      message.success(isEdit ? 'Actividad actualizada' : 'Actividad creada');
      void queryClient.invalidateQueries({ queryKey: ['activities'] });
      void queryClient.invalidateQueries({ queryKey: ['activity', saved.id] });
      navigate(`/actividades/${saved.id}`);
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'No se pudo guardar la actividad';
      message.error(msg);
    },
  });

  if (isEdit && isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={isEdit ? 'Editar actividad' : 'Nueva actividad'}
        crumbs={[
          { title: 'Actividades', to: '/actividades' },
          { title: isEdit ? `#${activityId}` : 'Nueva' },
        ]}
      />

      <Form layout="vertical" onFinish={handleSubmit((v) => mutation.mutate(v))}>
        <Card title="Detalle" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Tipo">
                    <LookupSelect
                      listName="activity_type"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={8}>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Inicio">
                    <DatePicker
                      showTime
                      style={{ width: '100%' }}
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(d) => field.onChange(d ? d.toISOString() : '')}
                      format="DD/MM/YYYY HH:mm"
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={8}>
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Fin">
                    <DatePicker
                      showTime
                      style={{ width: '100%' }}
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(d) => field.onChange(d ? d.toISOString() : '')}
                      format="DD/MM/YYYY HH:mm"
                    />
                  </Form.Item>
                )}
              />
            </Col>

            <Col xs={24} md={12}>
              <Controller
                name="accountId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Cuenta">
                    <AccountSelect value={field.value} onChange={field.onChange} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="personId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Contacto">
                    <PersonSelect
                      value={field.value}
                      onChange={field.onChange}
                      accountId={watchedAccountId ?? undefined}
                    />
                  </Form.Item>
                )}
              />
            </Col>

            <Col xs={24} md={12}>
              <Controller
                name="projectId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Proyecto">
                    <ProjectSelect
                      value={field.value}
                      onChange={field.onChange}
                      customerId={watchedAccountId ?? undefined}
                    />
                  </Form.Item>
                )}
              />
            </Col>

            <Col xs={24} md={12}>
              <Controller
                name="saleId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Venta">
                    <SaleSelect
                      value={field.value}
                      onChange={field.onChange}
                      accountId={watchedAccountId ?? undefined}
                    />
                  </Form.Item>
                )}
              />
            </Col>

            <Col xs={24} md={12}>
              <Controller
                name="assignedToId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Asignar a">
                    <UserSelect value={field.value} onChange={field.onChange} />
                  </Form.Item>
                )}
              />
            </Col>

            <Col xs={12} md={6}>
              <Controller
                name="closed"
                control={control}
                render={({ field }) => (
                  <Form.Item label="¿Cerrada?">
                    <Switch checked={field.value ?? false} onChange={field.onChange} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={12} md={6}>
              <Controller
                name="private"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Privada">
                    <Switch checked={field.value ?? false} onChange={field.onChange} />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>
        </Card>

        <Card title="Notas" style={{ marginBottom: 16 }}>
          <Controller
            name="notes"
            control={control}
            render={({ field, fieldState }) => (
              <Form.Item
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
                required
              >
                <Input.TextArea {...field} rows={6} placeholder="Detalles de la actividad…" />
              </Form.Item>
            )}
          />
        </Card>

        <Space>
          <Button type="primary" htmlType="submit" loading={mutation.isPending}>
            {isEdit ? 'Guardar cambios' : 'Crear actividad'}
          </Button>
          <Button onClick={() => navigate(-1)}>Cancelar</Button>
        </Space>
      </Form>
    </>
  );
}
